import React, { createContext, useCallback, useContext, useEffect } from 'react'
import PropTypes from 'prop-types'
import { v4 as uuid } from 'uuid'

import { useImmerReducerAsync } from 'hooks/useReducerAsync'
import sentinelV2Api from 'api/sentinelV2'

/**
 * Sentinel v2 conversation + saved-questions state.
 *
 * A conversation is an ordered list of turns; each user question yields a user
 * turn + a pending assistant turn that resolves to a `result`, `clarification`,
 * or `error` from the single synchronous /ask response (no polling). The whole
 * conversation renders as a single-column chat transcript.
 *
 * Saved questions are curated favourites (separate from conversation history);
 * a saved question re-runs its prompt through /ask. `conversations` is the list
 * of past conversations shown in the History tab. `ui` holds the transient
 * shell state (the library drawer + the customer inspector drawer).
 */
export const initialState = {
  conversationId: null,
  turns: [],
  loading: false,
  error: null,
  saved: { items: [], loading: false },
  conversations: { items: [], loading: false },
  ui: { libraryOpen: true, inspectorCustomerId: null },
}

export const actions = Object.freeze({
  appendQuestion: 'APPEND_QUESTION',
  resolveTurn: 'RESOLVE_TURN',
  failTurn: 'FAIL_TURN',
  setHistory: 'SET_HISTORY',
  reset: 'RESET',
  setSaved: 'SET_SAVED',
  setSavedLoading: 'SET_SAVED_LOADING',
  addSaved: 'ADD_SAVED',
  removeSaved: 'REMOVE_SAVED',
  setConversations: 'SET_CONVERSATIONS',
  setConversationsLoading: 'SET_CONVERSATIONS_LOADING',
  toggleLibrary: 'TOGGLE_LIBRARY',
  openInspector: 'OPEN_INSPECTOR',
  closeInspector: 'CLOSE_INSPECTOR',

  askAsync: 'ASK_ASYNC',
  loadConversationAsync: 'LOAD_CONVERSATION_ASYNC',
  fetchSavedAsync: 'FETCH_SAVED_ASYNC',
  saveQuestionAsync: 'SAVE_QUESTION_ASYNC',
  deleteSavedAsync: 'DELETE_SAVED_ASYNC',
  fetchConversationsAsync: 'FETCH_CONVERSATIONS_ASYNC',
  rerunAsync: 'RERUN_ASYNC',
})

const CUSTOMER_COLUMNS = [
  'customer_id',
  'first_name',
  'last_name',
  'address',
  'salutation',
  'email',
  'gender',
  'birth_year',
]
/**
 * Treat a result as a customer/audience table when it exposes at least one
 * customer-specific column. ResultTable separately requires an id/customer_id
 * value before a row can open the customer inspector.
 */
export function isAudienceTable(table) {
  if (!table || !Array.isArray(table.columns)) return false
  const columns = new Set(table.columns.map((column) => String(column).trim().toLowerCase()))
  return CUSTOMER_COLUMNS.some((column) => columns.has(column))
}

function mapResponseToTurn(response) {
  const turnIndex = response.turn_index ?? null
  if (response.type === 'clarification') {
    return { status: 'clarification', clarifyingQuestion: response.question, turnIndex }
  }
  if (response.type === 'error') {
    return { status: 'error', message: response.message, sql: response.sql, turnIndex }
  }
  return {
    status: 'result',
    table: response.table || null,
    chart: response.chart || null,
    aggregates: response.aggregates || null,
    sql: response.sql || null,
    explanation: response.explanation || null,
    turnIndex,
    isAudience: isAudienceTable(response.table),
  }
}

export const reducer = (state, action) => {
  switch (action.type) {
    case actions.appendQuestion: {
      const { question, pendingId } = action.payload
      state.turns.push({ id: uuid(), role: 'user', question })
      state.turns.push({ id: pendingId, role: 'assistant', status: 'pending', question })
      state.loading = true
      state.error = null
      return state
    }
    case actions.resolveTurn: {
      const { pendingId, response } = action.payload
      if (response.conversation_id) state.conversationId = response.conversation_id
      const idx = state.turns.findIndex((t) => t.id === pendingId)
      if (idx !== -1) {
        const question = state.turns[idx].question
        state.turns[idx] = {
          id: pendingId,
          role: 'assistant',
          question,
          ...mapResponseToTurn(response),
        }
      }
      state.loading = false
      return state
    }
    case actions.failTurn: {
      const { pendingId, message } = action.payload
      const idx = state.turns.findIndex((t) => t.id === pendingId)
      if (idx !== -1) {
        const question = state.turns[idx].question
        state.turns[idx] = { id: pendingId, role: 'assistant', status: 'error', message, question }
      }
      state.loading = false
      state.error = message
      return state
    }
    case actions.setHistory: {
      const { conversationId, turns } = action.payload
      state.conversationId = conversationId
      state.turns = turns
      state.loading = false
      state.error = null
      return state
    }
    case actions.reset: {
      // Start a fresh conversation but keep the saved/conversations lists;
      // always drop any open inspector.
      return {
        ...initialState,
        saved: state.saved,
        conversations: state.conversations,
        ui: { ...initialState.ui, libraryOpen: state.ui.libraryOpen },
      }
    }
    case actions.setSavedLoading: {
      state.saved.loading = action.payload
      return state
    }
    case actions.setSaved: {
      state.saved = { items: action.payload, loading: false }
      return state
    }
    case actions.addSaved: {
      state.saved.items = [
        action.payload,
        ...state.saved.items.filter((item) => item.id !== action.payload.id),
      ]
      state.saved.loading = false
      return state
    }
    case actions.removeSaved: {
      state.saved.items = state.saved.items.filter((item) => item.id !== action.payload)
      state.saved.loading = false
      return state
    }
    case actions.setConversationsLoading: {
      state.conversations.loading = action.payload
      return state
    }
    case actions.setConversations: {
      state.conversations = { items: action.payload, loading: false }
      return state
    }
    case actions.toggleLibrary: {
      state.ui.libraryOpen = !state.ui.libraryOpen
      return state
    }
    case actions.openInspector: {
      state.ui.inspectorCustomerId = action.payload
      return state
    }
    case actions.closeInspector: {
      state.ui.inspectorCustomerId = null
      return state
    }
    default:
      return state
  }
}

const DEFAULT_ERROR = 'Something went wrong. Please try again.'
function readErrorMessage(response) {
  return (
    response?.error?.response?.data?.message ||
    (typeof response?.error === 'string' ? response.error : null) ||
    DEFAULT_ERROR
  )
}

function askAsync({ dispatch, getState, signal }) {
  return async (action) => {
    const { question } = action.payload
    const pendingId = uuid()
    dispatch({ type: actions.appendQuestion, payload: { question, pendingId } })
    const { conversationId } = getState()
    const response = await sentinelV2Api.ask({ question, conversationId, signal })
    if (response.error) {
      dispatch({
        type: actions.failTurn,
        payload: { pendingId, message: readErrorMessage(response) },
      })
      return
    }
    dispatch({ type: actions.resolveTurn, payload: { pendingId, response: response.data } })
  }
}

export function rerunAsync({ dispatch, getState, signal }) {
  return async (action) => {
    const { question } = action.payload
    const pendingId = uuid()
    const { conversationId } = getState()
    // A rerun is stored by the API as a new turn. Append it immediately so the
    // live transcript has the same order as the conversation after reopening.
    dispatch({ type: actions.appendQuestion, payload: { question, pendingId } })
    const response = await sentinelV2Api.ask({ question, conversationId, signal })
    if (response.error) {
      dispatch({
        type: actions.failTurn,
        payload: { pendingId, message: readErrorMessage(response) },
      })
      return
    }
    dispatch({ type: actions.resolveTurn, payload: { pendingId, response: response.data } })
  }
}

function loadConversationAsync({ dispatch }) {
  return async (action) => {
    const { conversationId } = action.payload
    const response = await sentinelV2Api.fetchConversation(conversationId)
    if (response.error || !response.data) return
    const turns = []
    ;(response.data.turns || []).forEach((t) => {
      turns.push({ id: uuid(), role: 'user', question: t.question })
      turns.push({
        id: uuid(),
        role: 'assistant',
        status: 'history',
        question: t.question,
        sql: t.generated_sql,
        historyStatus: t.status,
        rowCount: t.row_count,
        turnIndex: t.turn_index,
      })
    })
    dispatch({ type: actions.setHistory, payload: { conversationId, turns } })
  }
}

function fetchSavedAsync({ dispatch }) {
  return async () => {
    dispatch({ type: actions.setSavedLoading, payload: true })
    const response = await sentinelV2Api.fetchSaved()
    dispatch({
      type: actions.setSaved,
      payload: response.error ? [] : response.data?.saved || response.data || [],
    })
  }
}

function fetchConversationsAsync({ dispatch }) {
  return async () => {
    dispatch({ type: actions.setConversationsLoading, payload: true })
    const response = await sentinelV2Api.fetchConversations()
    dispatch({
      type: actions.setConversations,
      payload: response.error ? [] : response.data?.conversations || response.data || [],
    })
  }
}

function saveQuestionAsync({ dispatch }) {
  return async (action) => {
    const { name, question } = action.payload
    const response = await sentinelV2Api.saveQuestion({ name, question })
    if (!response.error) {
      const item = response.data?.saved || response.data
      if (item?.id) {
        dispatch({ type: actions.addSaved, payload: { ...item, name, question } })
      } else {
        fetchSavedAsync({ dispatch })()
      }
    }
  }
}

function deleteSavedAsync({ dispatch }) {
  return async (action) => {
    const response = await sentinelV2Api.deleteSaved(action.payload.id)
    if (!response.error) {
      dispatch({ type: actions.removeSaved, payload: action.payload.id })
    }
  }
}

const asyncActionHandlers = {
  [actions.askAsync]: askAsync,
  [actions.loadConversationAsync]: loadConversationAsync,
  [actions.fetchSavedAsync]: fetchSavedAsync,
  [actions.saveQuestionAsync]: saveQuestionAsync,
  [actions.deleteSavedAsync]: deleteSavedAsync,
  [actions.fetchConversationsAsync]: fetchConversationsAsync,
  [actions.rerunAsync]: rerunAsync,
}

const SentinelChatContext = createContext(null)

function initializeState(state) {
  const libraryOpen =
    typeof window === 'undefined' || typeof window.matchMedia !== 'function'
      ? state.ui.libraryOpen
      : window.matchMedia('(min-width: 992px)').matches

  return {
    ...state,
    turns: [],
    saved: { ...state.saved, items: [] },
    conversations: { ...state.conversations, items: [] },
    ui: { ...state.ui, libraryOpen },
  }
}

export default function SentinelChatProvider({ children }) {
  const [state, dispatch] = useImmerReducerAsync(
    reducer,
    initialState,
    initializeState,
    asyncActionHandlers,
  )

  const ask = useCallback(
    (question) => {
      const trimmed = (question || '').trim()
      if (!trimmed) return
      dispatch({ type: actions.askAsync, payload: { question: trimmed } })
    },
    [dispatch],
  )

  const rerunTurn = useCallback(
    (id, question) =>
      id && question && dispatch({ type: actions.rerunAsync, payload: { id, question } }),
    [dispatch],
  )
  const newConversation = useCallback(() => dispatch({ type: actions.reset }), [dispatch])
  const loadConversation = useCallback(
    (conversationId) =>
      conversationId &&
      dispatch({ type: actions.loadConversationAsync, payload: { conversationId } }),
    [dispatch],
  )
  const fetchSaved = useCallback(() => dispatch({ type: actions.fetchSavedAsync }), [dispatch])
  const saveQuestion = useCallback(
    (name, question) => dispatch({ type: actions.saveQuestionAsync, payload: { name, question } }),
    [dispatch],
  )
  const deleteSavedQuestion = useCallback(
    (id) => dispatch({ type: actions.deleteSavedAsync, payload: { id } }),
    [dispatch],
  )

  const fetchConversations = useCallback(
    () => dispatch({ type: actions.fetchConversationsAsync }),
    [dispatch],
  )
  const toggleLibrary = useCallback(() => dispatch({ type: actions.toggleLibrary }), [dispatch])
  const openInspector = useCallback(
    (customerId) => dispatch({ type: actions.openInspector, payload: customerId }),
    [dispatch],
  )
  const closeInspector = useCallback(() => dispatch({ type: actions.closeInspector }), [dispatch])

  // Keep the History list fresh: load once, and refresh whenever the active
  // conversation changes or a new turn resolves in it.
  const conversationId = state.conversationId
  const assistantCount = state.turns.filter(
    (t) => t.role === 'assistant' && t.status !== 'pending',
  ).length
  useEffect(() => {
    fetchConversations()
  }, [fetchConversations, conversationId, assistantCount])

  const value = {
    ...state,
    ask,
    answerClarification: ask,
    rerunTurn,
    newConversation,
    loadConversation,
    fetchSaved,
    saveQuestion,
    deleteSavedQuestion,
    fetchConversations,
    toggleLibrary,
    openInspector,
    closeInspector,
  }

  return <SentinelChatContext.Provider value={value}>{children}</SentinelChatContext.Provider>
}

SentinelChatProvider.propTypes = { children: PropTypes.node.isRequired }

export function useSentinelChat() {
  const ctx = useContext(SentinelChatContext)
  if (ctx === null) throw new Error('useSentinelChat must be used within a SentinelChatProvider')
  return ctx
}
